import { useEffect, useState } from "react";
import { RedButton } from "../../../components";
import {
  IBulkDeleteResult,
  bulkErrorMessage,
  useVouchers,
} from "../../../store/useVouchers";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The vouchers to delete. */
  ids: string[];
  onDone: (result: IBulkDeleteResult) => void;
}

/**
 * Deletes the selected vouchers after showing what will happen: how many go,
 * how many are kept because customers already have them, and which live
 * challenges are left without vouchers until a new batch is added.
 */
export const BulkDeleteVouchersModal = ({ open, onClose, ids, onDone }: Props) => {
  const { bulkDeleteVouchers } = useVouchers();
  const [preview, setPreview] = useState<IBulkDeleteResult | null>(null);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Keyed on the ids' text so a new array with the same ids does not refetch.
  const idsKey = ids.join(",");
  useEffect(() => {
    if (!open || !ids.length) {
      setPreview(null);
      setError("");
      setIsDeleting(false);
      return;
    }
    let cancelled = false;
    setPreview(null);
    setError("");
    bulkDeleteVouchers({ ids, dryRun: true })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(
            bulkErrorMessage(
              e,
              "Could not count the selected vouchers. Please try again."
            )
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsKey]);

  if (!open) return null;

  const canDelete = !isDeleting && (preview?.willDelete ?? 0) > 0;

  const handleDelete = async () => {
    if (!canDelete) return;
    setIsDeleting(true);
    setError("");
    try {
      const result = await bulkDeleteVouchers({ ids });
      onDone(result);
    } catch (e: any) {
      setError(
        bulkErrorMessage(e, "The vouchers could not be deleted. Nothing was deleted.")
      );
      setIsDeleting(false);
    }
  };

  const plural = (n: number, one: string, many: string) =>
    `${n} ${n === 1 ? one : many}`;

  return (
    <>
      <div className="justify-center items-center z-[10000000000] backdrop-blur-sm flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none">
        <div className="relative w-10/12 sm:w-1/3 my-0 sm:my-6 mx-auto max-w-2xl">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col max-h-[85vh] bg-white overflow-y-auto w-full outline-none focus:outline-none">
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <h3 className="text-2xl font-semibold font-satoshi text-dark">
                Delete {plural(ids.length, "voucher", "vouchers")}?
              </h3>
              <button
                className="text-gray-500"
                onClick={onClose}
                disabled={isDeleting}
              >
                ✕
              </button>
            </div>
            <div className="relative p-6 flex-auto space-y-3 text-sm text-dark">
              <div data-testid="bulk-delete-preview" className="space-y-3">
                {!preview && !error ? <p>Counting the selected vouchers...</p> : null}
                {preview ? (
                  <>
                    <p>
                      <strong>{plural(preview.willDelete, "voucher", "vouchers")}</strong>{" "}
                      will be deleted. This cannot be undone.
                    </p>
                    {preview.skippedIssued > 0 ? (
                      <p>
                        {plural(preview.skippedIssued, "voucher is", "vouchers are")}{" "}
                        already given to customers and will be kept: they stay in
                        the customers' wallets.
                      </p>
                    ) : null}
                    {preview.skippedDrop > 0 ? (
                      <p>
                        {plural(preview.skippedDrop, "drop voucher is", "drop vouchers are")}{" "}
                        skipped.
                      </p>
                    ) : null}
                    {preview.notFound > 0 ? (
                      <p>
                        {plural(preview.notFound, "voucher no longer exists", "vouchers no longer exist")}.
                      </p>
                    ) : null}
                    {preview.emptiedChallenges.length ? (
                      <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">
                        After this, <strong>{preview.emptiedChallenges.join(", ")}</strong>{" "}
                        {preview.emptiedChallenges.length === 1 ? "has" : "have"} no
                        vouchers left. New participants will not see it, and
                        customers who complete it in the meantime wait for a
                        voucher or get none. Add the new vouchers straight after
                        deleting.
                      </p>
                    ) : null}
                  </>
                ) : null}
              </div>
              {error ? (
                <p className="text-red-600" data-testid="bulk-error">
                  {error}
                </p>
              ) : null}
            </div>
            <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
              <button
                type="button"
                className="mr-4 font-satoshi font-medium text-base text-dark px-4 py-1"
                onClick={() => !isDeleting && onClose()}
              >
                Cancel
              </button>
              <RedButton
                label={
                  isDeleting
                    ? "Deleting..."
                    : preview
                      ? `Delete ${plural(preview.willDelete, "voucher", "vouchers")}`
                      : "Delete vouchers"
                }
                className={canDelete ? "" : "opacity-50 pointer-events-none"}
                onClick={handleDelete}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};
